import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel, Spinner } from "../components/ui-kit";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { MenuVisibility } from "../components/settings/MenuVisibility";
import { VipPageEditor } from "../components/settings/VipPageEditor";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [brand, setBrand] = useState("");
  const [tagline, setTagline] = useState("");
  const [cfg, setCfg] = useState({});
  const [weights, setWeights] = useState({});
  const [tg, setTg] = useState({});
  const [tzone, setTzone] = useState("Asia/Dubai");
  const [briefHour, setBriefHour] = useState(8);
  const [pixel, setPixel] = useState("");
  const [promoEvery, setPromoEvery] = useState(6);
  const [promoMins, setPromoMins] = useState(10);
  const [tgStatus, setTgStatus] = useState(null);

  useEffect(() => {
    client.get("/settings").then(({ data }) => {
      setS(data); setBrand(data.brand_name || ""); setTagline(data.tagline || "");
      setCfg(data.signal_config || {}); setWeights(data.score_weights || {});
      setTg(data.telegram || {}); setTzone(data.timezone || "Asia/Dubai"); setBriefHour(data.daily_brief_hour ?? 8); setPixel(data.meta_pixel_id || ""); setPromoEvery(data.promo_every ?? 6); setPromoMins(data.promo_strip_minutes ?? 10);
    });
    client.get("/telegram/status").then(({ data }) => setTgStatus(data)).catch(() => {});
    client.get("/market/status").then(({ data }) => setMktStatus(data)).catch(() => {});
  }, []);
  const [mktStatus, setMktStatus] = useState(null);

  const save = async () => {
    try {
      await client.patch("/settings", { brand_name: brand, tagline, signal_config: cfg, score_weights: weights,
        telegram: tg, timezone: tzone, daily_brief_hour: Number(briefHour), meta_pixel_id: pixel.trim(), promo_every: Math.min(50, Math.max(1, Number(promoEvery) || 6)), promo_strip_minutes: Math.min(1440, Math.max(0, Number(promoMins) || 0)) });
      toast.success("Settings saved — brand name updates across app");
    } catch { toast.error("Failed"); }
  };

  const testTg = async (channel) => {
    const t = toast.loading(`Sending test to ${channel} channel…`);
    try { const { data } = await client.post("/telegram/test", { channel }); toast.dismiss(t);
      if (data.ok) toast.success(`Sent to ${data.chat_id}`); else toast.error(`Telegram: ${data.error}`);
    } catch { toast.dismiss(t); toast.error("Test failed"); }
  };

  const [discovered, setDiscovered] = useState(null);
  const discover = async () => {
    const t = toast.loading("Scanning for channels the bot can see…");
    try { const { data } = await client.get("/telegram/discover"); toast.dismiss(t);
      if (!data.ok) return toast.error(data.error);
      setDiscovered(data.chats);
      if (!data.chats.length) toast.info("No channels found yet — post any message in the private channel first, then scan again.");
    } catch { toast.dismiss(t); toast.error("Scan failed"); }
  };

  if (!s) return <Layout title="Settings"><Spinner /></Layout>;
  const canEdit = user?.role === "super_admin" || user?.role === "admin";

  const toggles = [
    ["auto_generate", "Auto-generate signals"], ["auto_publish_dashboard", "Auto-publish to dashboard"],
    ["auto_publish_premium_tg", "Auto-publish to Premium Telegram"], ["auto_publish_free_tg", "Auto-publish to Free Telegram"],
    ["require_approval", "Require human approval"], ["approve_on_high_news", "Force approval when News Risk = High"],
    ["auto_daily_brief", "Send daily brief to Telegram (scheduler)"],
    ["auto_detect_strategies", "Auto-detect setups from strategies (hourly)"],
    ["auto_publish_scheduled", "Auto-publish calendar posts to Telegram"],
  ];

  const TIMEZONES = ["Asia/Dubai", "Asia/Kolkata", "Asia/Karachi", "Asia/Singapore", "Europe/London", "America/New_York", "UTC"];

  return (
    <Layout title="Admin Settings">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <Panel title="Branding">
          <div className="p-4 space-y-4">
            <div><label className="text-xs uppercase tracking-wider text-slate-500">Brand Name</label>
              <input data-testid="setting-brand" value={brand} onChange={(e) => setBrand(e.target.value)} disabled={!canEdit} className="w-full mt-1.5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none disabled:opacity-60" /></div>
            <div><label className="text-xs uppercase tracking-wider text-slate-500">Tagline</label>
              <input value={tagline} onChange={(e) => setTagline(e.target.value)} disabled={!canEdit} className="w-full mt-1.5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none disabled:opacity-60" /></div>
          </div>
        </Panel>

        <Panel title="Signal Automation">
          <div className="p-4 space-y-2">
            {toggles.map(([k, label]) => (
              <label key={k} className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
                <span className="text-sm text-slate-300">{label}</span>
                <button data-testid={`toggle-${k}`} onClick={() => canEdit && setCfg({ ...cfg, [k]: !cfg[k] })} type="button"
                  className={`w-10 h-5 shrink-0 relative transition-colors duration-200 ${cfg[k] ? "bg-gold" : "bg-white/10"}`}>
                  <span className={`absolute top-0.5 left-0 w-4 h-4 bg-white transition-transform duration-200 ${cfg[k] ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </label>
            ))}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <NumField label="Min confidence" v={cfg.min_confidence} onChange={(v) => setCfg({ ...cfg, min_confidence: v })} disabled={!canEdit} />
              <NumField label="Min R:R (x10)" v={cfg.min_rr} step="0.1" onChange={(v) => setCfg({ ...cfg, min_rr: v })} disabled={!canEdit} />
              <NumField label="Max signals/day" v={cfg.max_signals_per_day} onChange={(v) => setCfg({ ...cfg, max_signals_per_day: v })} disabled={!canEdit} />
              <NumField label="News block (min)" v={cfg.news_block_minutes} onChange={(v) => setCfg({ ...cfg, news_block_minutes: v })} disabled={!canEdit} />
            </div>
          </div>
        </Panel>

        <Panel title="Signal Score Weights" className="lg:col-span-2">
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(weights).map(([k, v]) => (
              <NumField key={k} label={k} v={v} onChange={(val) => setWeights({ ...weights, [k]: val })} disabled={!canEdit} />
            ))}
          </div>
          <div className="px-4 pb-4 text-xs text-slate-500">Bands: &lt;60 Reject · 60-69 Watchlist · 70-79 Valid · 80+ High Confluence. Confidence is not a guarantee of profit.</div>
        </Panel>

        <Panel title="Telegram Publishing" testid="telegram-settings">
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs">
              <span className={`w-2 h-2 rounded-full ${tgStatus?.configured ? "bg-bull" : "bg-bear"}`} />
              <span className="text-slate-400">Bot {tgStatus?.configured ? "connected" : "not configured"}</span>
            </div>
            <div><label className="text-xs uppercase tracking-wider text-slate-500">Free Channel</label>
              <div className="flex gap-2 mt-1.5">
                <input data-testid="tg-free-channel" value={tg.free_channel || ""} onChange={(e) => setTg({ ...tg, free_channel: e.target.value })} disabled={!canEdit} placeholder="@channel or -100..." className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none disabled:opacity-60" />
                <button type="button" onClick={() => testTg("free")} data-testid="test-tg-free" className="px-3 py-2 text-xs border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200">Test</button>
              </div>
            </div>
            <div><label className="text-xs uppercase tracking-wider text-slate-500">Premium Channel</label>
              <div className="flex gap-2 mt-1.5">
                <input data-testid="tg-premium-channel" value={tg.premium_channel || ""} onChange={(e) => setTg({ ...tg, premium_channel: e.target.value })} disabled={!canEdit} placeholder="@channel or -100..." className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none disabled:opacity-60" />
                <button type="button" onClick={() => testTg("premium")} data-testid="test-tg-premium" className="px-3 py-2 text-xs border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200">Test</button>
              </div>
            </div>
            <p className="text-[11px] text-slate-500">⚠️ Add the bot as an <b>admin</b> in both channels, otherwise sends fail with "bot is not a member".</p>
            <div className="border-t border-[color:var(--border-subtle)] pt-3">
              <label className="text-xs uppercase tracking-wider text-slate-500">Public Site URL (your domain)</label>
              <input data-testid="tg-site-url" value={tg.open_account_url || ""} onChange={(e) => setTg({ ...tg, open_account_url: e.target.value })} disabled={!canEdit} placeholder="https://fatehfx.com/open-account" className="w-full mt-1.5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none disabled:opacity-60" />
              <p className="text-[11px] text-slate-500 mt-1">Used for the <b>Open Account</b> button in channel posts &amp; the daily summary. Point it to your landing page.</p>
            </div>
            <div className="border-t border-[color:var(--border-subtle)] pt-3">
              <label className="text-xs uppercase tracking-wider text-slate-500">Free Telegram Channel Join Link</label>
              <input data-testid="tg-free-join-url" value={tg.free_join_url || ""} onChange={(e) => setTg({ ...tg, free_join_url: e.target.value })} disabled={!canEdit} placeholder="Auto-fetched from bot (https://t.me/+...)" className="w-full mt-1.5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none disabled:opacity-60" />
            </div>
            <div className="border-t border-[color:var(--border-subtle)] pt-3">
              <label className="text-xs uppercase tracking-wider text-slate-500">Meta (FB/IG) Pixel ID</label>
              <input data-testid="meta-pixel-id" value={pixel} onChange={(e) => setPixel(e.target.value.replace(/\D/g, ""))} disabled={!canEdit} placeholder="e.g. 1234567890123456" className="w-full mt-1.5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none disabled:opacity-60" />
              <p className="text-[11px] text-slate-500 mt-1">Loads on the <b>/join</b> ad landing page: tracks PageView, Lead (form submit) and Contact (WhatsApp/Telegram clicks).</p>
            </div>
            <div className="border-t border-[color:var(--border-subtle)] pt-3 grid grid-cols-2 gap-3" data-testid="promo-settings">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500">Promo every N-th message</label>
                <input data-testid="promo-every" type="number" min="1" max="50" value={promoEvery} onChange={(e) => setPromoEvery(e.target.value)} disabled={!canEdit} className="w-full mt-1.5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none disabled:opacity-60" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500">Remove promo after (min)</label>
                <input data-testid="promo-strip-minutes" type="number" min="0" max="1440" value={promoMins} onChange={(e) => setPromoMins(e.target.value)} disabled={!canEdit} className="w-full mt-1.5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none disabled:opacity-60" />
              </div>
              <p className="col-span-2 text-[11px] text-slate-500">Buttons + promo lines go on every N-th Telegram post (1 = every post). In the <b>Free</b> channel they are removed after the set minutes (0 = never remove); the signal text stays.</p>
            </div>
            <div className="border-t border-[color:var(--border-subtle)] pt-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-400">Private channel? It has no @username — use its numeric ID.</span>
                <button type="button" onClick={discover} data-testid="tg-discover" className="px-2.5 py-1 text-xs border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200">Find ID</button>
              </div>
              {discovered && (
                <div className="mt-2 space-y-1" data-testid="tg-discovered">
                  {discovered.length === 0 ? <div className="text-[11px] text-slate-500">Nothing yet. Add bot as admin → post a message in the channel → Find ID again.</div> :
                    discovered.map((c) => (
                      <button key={c.id} type="button" onClick={() => { setTg({ ...tg, premium_channel: String(c.id) }); toast.success(`Set premium channel to ${c.id}`); }}
                        className="w-full flex items-center justify-between px-2 py-1.5 bg-white/5 hover:bg-white/10 transition-colors duration-150 text-xs">
                        <span className="text-slate-200">{c.title} <span className="text-slate-500">({c.type})</span></span>
                        <span className="font-mono text-gold">{c.id}</span>
                      </button>
                    ))}
                </div>
              )}
              <p className="text-[11px] text-slate-500 mt-1.5">Steps for a private premium channel: (1) add the bot as <b>admin</b>, (2) post any message in it, (3) click <b>Find ID</b>, (4) tap the result to fill the Premium field, (5) Save.</p>
            </div>
          </div>
        </Panel>

        <Panel title="Scheduler & Data" testid="scheduler-settings">
          <div className="p-4 space-y-4">
            <div><label className="text-xs uppercase tracking-wider text-slate-500">Timezone</label>
              <select data-testid="setting-timezone" value={tzone} onChange={(e) => setTzone(e.target.value)} disabled={!canEdit} className="w-full mt-1.5 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none disabled:opacity-60">
                {TIMEZONES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <NumField label="Daily brief hour (0-23)" v={briefHour} onChange={setBriefHour} disabled={!canEdit} />
            <div className="text-[11px] text-slate-500 leading-relaxed">
              Daily brief posts to Telegram at <span className="font-mono text-slate-300">{String(briefHour).padStart(2, "0")}:00 {tzone}</span> when the toggle is on. Approved signals publish to Telegram instantly per the toggles above.
            </div>
            {mktStatus && (
              <div className="text-[11px] text-slate-500 border-t border-[color:var(--border-subtle)] pt-3">
                <div className="text-slate-400 mb-1">Market data (Alpha Vantage)</div>
                {mktStatus.note}
              </div>
            )}
          </div>
        </Panel>
        {canEdit && <VipPageEditor />}
        {canEdit && <MenuVisibility />}
      </div>

      {canEdit && <button data-testid="save-settings" onClick={save} className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2 mt-5"><Save size={16} />Save Settings</button>}
    </Layout>
  );
}
const NumField = ({ label, v, onChange, step = "1", disabled }) => (
  <div><label className="text-xs uppercase tracking-wider text-slate-500">{label}</label>
    <input type="number" step={step} value={v ?? ""} onChange={(e) => onChange(Number(e.target.value))} disabled={disabled}
      className="w-full mt-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm font-mono text-white focus:border-gold focus:outline-none disabled:opacity-60" /></div>
);
